import { closestCenter, DndContext, KeyboardSensor, PointerSensor, useSensor, useSensors } from '@dnd-kit/core'
import { arrayMove, SortableContext, sortableKeyboardCoordinates, verticalListSortingStrategy } from '@dnd-kit/sortable'

//UI
import { camelToWords } from '@/utils/stringHelper'
import { Button, Card, CardBody, CardHeader, Flex, Heading, Spacer, Tag, TagLeftIcon } from '@chakra-ui/react'
import { useMemo } from 'react'
import { IoRemoveOutline } from 'react-icons/io5'
import ColumnMenuItem from './ColumnMenuItem'
import { getAllMyOfferDisplayGroupByLevel } from '@/features/offers/constants/offerList.constant'
import { MyOfferDisplayLevel } from '@/features/tableProfiles/constants/tableProfile.constant'

export const formatDisplayLevelTag = (displayLevel) => {
  switch (displayLevel) {
    case MyOfferDisplayLevel.GCPR:
      return 'GCPR'
    case MyOfferDisplayLevel.GCPR_LINE_ITEM:
      return 'GCPR Line Item'
    default:
      return camelToWords(displayLevel)
  }
}
const AppliedColumnCard = ({
  columns,
  setColumns,
  handleRemoveColumn,
  handleRemoveAllColumns,
  handleRemoveColumnByGroup,
  displayLevel,
}) => {
  const availableColumnGroups = useMemo(() => {
    const groups = {}
    columns.forEach((column) => {
      const displayLevel = column?.columnDef?.meta?.displayLevel ?? ''
      const displayColor = column?.columnDef?.meta?.displayColor ?? ''
      if (displayLevel !== '' && displayColor !== '' && !groups[displayLevel]) {
        groups[displayLevel] = {
          displayLevel: displayLevel,
          displayColor: displayColor,
        }
      }
    })
    return groups
  }, [columns])

  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  )

  const handleDragEnd = (event) => {
    const { active, over } = event

    if (active.id !== over.id) {
      setColumns((items) => {
        const oldIndex = items.findIndex((item) => item.id === active.id)
        const newIndex = items.findIndex((item) => item.id === over.id)

        return arrayMove(items, oldIndex, newIndex)
      })
    }
  }

  return (
    <Card w="50%" overflowY={'scroll'}>
      <Flex direction={'column'} px={3}>
        <CardHeader>
          <Flex>
            <Heading size="md">Applied Columns</Heading>
            <Spacer />
            <Button
              color="red.300"
              borderColor={'red.300'}
              variant={'outline'}
              _hover={{ color: 'white', bg: 'red.300' }}
              onClick={handleRemoveAllColumns}
            >
              Remove All
            </Button>
          </Flex>
          <Flex gap="2">
            {Object.values(availableColumnGroups).map((group) => (
              <Tag
                key={group.displayLevel}
                size="md"
                colorScheme={group.displayColor}
                variant="solid"
                _hover={{ cursor: 'pointer', color: 'blackAlpha.500' }}
                onClick={() => handleRemoveColumnByGroup(group.displayLevel)}
              >
                <TagLeftIcon boxSize="12px" as={IoRemoveOutline} />
                {formatDisplayLevelTag(group.displayLevel)}
              </Tag>
            ))}
          </Flex>
        </CardHeader>
        <CardBody>
          <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
            <SortableContext items={columns} strategy={verticalListSortingStrategy}>
              {columns.map((column) => (
                <ColumnMenuItem
                  key={column.id}
                  id={column.id}
                  canRemove={column.getCanHide()}
                  handleRemoveColumn={() => handleRemoveColumn(column)}
                  displayLevel={formatDisplayLevelTag(column?.columnDef?.meta?.displayLevel ?? '')}
                  displayColor={column?.columnDef?.meta?.displayColor}
                >
                  {/* FIXME: find better solution for display vs column name */}
                  {column?.columnDef?.header
                    ? column.columnDef.header({ header: { id: column.id } })
                    : camelToWords(column.id)}
                </ColumnMenuItem>
              ))}
            </SortableContext>
          </DndContext>
        </CardBody>
      </Flex>
    </Card>
  )
}

export default AppliedColumnCard
